import type { AddFromTemplatesResult, TemplateCatalog } from "@fieldpath/shared";
import { useEffect, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { DateHint, PAST_DATE_BOX } from "./DateHint";
import { KindTag } from "./KindTag";

/**
 * ADD FROM TEMPLATES — the picker on the project page (E2 row 4b).
 *
 * Deliverable templates bring their tasks with them; task templates can also
 * be added on their own, in no deliverable (field work, mostly). The server
 * does it in one transaction and SAYS what it skipped (E2.14), so this form
 * shows, before anything is pressed, which tasks would be skipped and why —
 * from the same `unusableBecause` the server decides with — and afterwards
 * reports what was made, what was skipped and which blocks wait to be linked.
 *
 * Inactive templates are not offered at all: switching one off on `/admin` is
 * how it stops being offered (E2 7a).
 *
 * **"Tasks on their own" lists only what is meant to be used alone** (Jeremy,
 * 13 Oct 2026): a task template that is part of a deliverable template is
 * offered inside it, unless an admin switched "Offer on its own" on. The
 * server decides (`offeredOnItsOwn`); templates built as a project will make
 * many parts, and listing each alone would bury the few that are not. The
 * list is grouped by workflow, and one search filters both columns.
 */
export function AddFromTemplates({
  fetcher,
  projectId,
  onAdded,
  onDone,
}: {
  fetcher: Fetcher;
  projectId: string;
  /** The work exists now: reload the page beneath the report. */
  onAdded: () => void;
  /** Closed, after reading the report or without adding anything. */
  onDone: () => void;
}) {
  const [catalog, setCatalog] = useState<TemplateCatalog | null>(null);
  const [deliverableIds, setDeliverableIds] = useState<ReadonlySet<string>>(new Set());
  const [taskIds, setTaskIds] = useState<ReadonlySet<string>>(new Set());
  const [dueDate, setDueDate] = useState("");
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AddFromTemplatesResult | null>(null);

  useEffect(() => {
    void api
      .templateCatalog(fetcher)
      .then(setCatalog)
      .catch((e: Error) => setError(e.message));
  }, [fetcher]);

  const toggle =
    (set: (fn: (s: ReadonlySet<string>) => ReadonlySet<string>) => void, id: string) => () =>
      set((s) => {
        const next = new Set(s);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      setResult(
        await api.addFromTemplates(fetcher, projectId, {
          deliverableTemplateIds: [...deliverableIds],
          taskTemplateIds: [...taskIds],
          dueDate: dueDate || null,
        }),
      );
      onAdded();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (result) return <AddedReport result={result} onDone={onDone} />;

  if (!catalog) {
    return (
      <div className="card">
        <h2>Add from templates</h2>
        {error ? <div className="error">{error}</div> : <p className="muted">Loading…</p>}
      </div>
    );
  }

  const taskById = new Map(catalog.taskTemplates.map((t) => [t.id, t]));
  const q = query.trim().toLowerCase();
  const has = (...texts: (string | null)[]) => q === "" || texts.some((t) => t !== null && t.toLowerCase().includes(q));
  const deliverables = catalog.deliverableTemplates.filter(
    (d) => d.isActive && has(d.name, d.typeName, ...d.tasks.map((t) => t.title)),
  );
  const tasks = catalog.taskTemplates.filter((t) => t.isActive && t.offeredOnItsOwn && has(t.name, t.workflowName));
  // Grouped by workflow, so a long list still reads: all the GIS work together.
  const byWorkflow = [...new Set(tasks.map((t) => t.workflowName))]
    .sort((a, b) => a.localeCompare(b))
    .map((w) => ({ workflow: w, tasks: tasks.filter((t) => t.workflowName === w).sort((a, b) => a.name.localeCompare(b.name)) }));
  const chosen = deliverableIds.size + taskIds.size;
  // E11.6: said BEFORE pressing. Which chosen task templates date their
  // stages from the due date -- the same `countsDates` the server counts by.
  const chosenTaskTemplates = [
    ...catalog.deliverableTemplates
      .filter((d) => deliverableIds.has(d.id))
      .flatMap((d) => d.tasks.map((t) => taskById.get(t.taskTemplateId))),
    ...[...taskIds].map((id) => taskById.get(id)),
  ];
  const countsDates = chosenTaskTemplates.some((t) => t?.countsDates && t.unusableBecause === null);

  return (
    <form className="card" onSubmit={submit}>
      <h2>Add from templates</h2>
      <p className="muted" style={{ marginTop: -8 }}>
        Each task arrives on its workflow with the template's instructions, figures,
        hours and people. Owners come from the project's PM and TM.
      </p>
      {error ? <div className="error">{error}</div> : null}

      <div className="aft-search">
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" />
          <path d="M10.5 10.5l3.5 3.5" />
        </svg>
        <input
          aria-label="Find a template"
          placeholder="Find a template by name, type or workflow"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="aft-cols">
        <fieldset className="aft-group">
          <legend>Deliverables, with their tasks</legend>
          {deliverables.length === 0 ? (
            <p className="muted">
              {q ? "No deliverable template matches." : "No deliverable templates are switched on."}
            </p>
          ) : (
            deliverables.map((d) => (
              <label key={d.id} className="aft-item">
                <input
                  type="checkbox"
                  checked={deliverableIds.has(d.id)}
                  onChange={toggle(setDeliverableIds, d.id)}
                />
                <span>
                  <span className="aft-name">
                    <KindTag kind="deliverable" size="row" />
                    {d.name}
                  </span>
                  {d.typeName ? <span className="muted"> · {d.typeName}</span> : null}
                  <span className="aft-tasks">
                    {d.tasks.map((t) => {
                      const why = taskById.get(t.taskTemplateId)?.unusableBecause ?? null;
                      return (
                        <span key={t.taskTemplateId} className={why ? "aft-skip" : undefined}>
                          {t.title}
                          {why ? ` — skipped: ${why}` : ""}
                        </span>
                      );
                    })}
                  </span>
                </span>
              </label>
            ))
          )}
        </fieldset>

        <fieldset className="aft-group">
          <legend>Tasks on their own</legend>
          <p className="muted aft-note">
            Parts of a deliverable are offered inside it. An admin can also offer one here.
          </p>
          {byWorkflow.length === 0 ? (
            <p className="muted">{q ? "No task template matches." : "No task templates are offered on their own."}</p>
          ) : null}
          {byWorkflow.map((g) => (
            <div key={g.workflow} className="aft-workflow">
              <div className="aft-workflow-name">{g.workflow}</div>
              {g.tasks.map((t) => (
                <label key={t.id} className={`aft-item ${t.unusableBecause ? "is-off" : ""}`}>
                  <input
                    type="checkbox"
                    checked={taskIds.has(t.id)}
                    disabled={t.unusableBecause !== null}
                    onChange={toggle(setTaskIds, t.id)}
                  />
                  <span>
                    <span className="aft-name">
                      <KindTag kind="task" size="row" />
                      {t.name}
                    </span>
                    {t.unusableBecause ? (
                      <span className="aft-tasks">
                        <span className="aft-skip">Not available: {t.unusableBecause}</span>
                      </span>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>
          ))}
        </fieldset>
      </div>

      <div className="field" style={{ maxWidth: 260 }}>
        <label htmlFor="aft-due">Due to client — optional</label>
        <input
          id="aft-due"
          type="date"
          {...PAST_DATE_BOX}
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
        <DateHint />
        <p className="muted" style={{ marginTop: 4 }}>
          Sets each new deliverable's client date and each task's due date, and
          counts the stage dates back from it. Leave it blank to set dates later.
        </p>
      </div>
      {/* Full width, outside the narrow date field: a sentence this long
          wrapped to five lines in it. */}
      {countsDates && dueDate === "" ? (
        <p className="warn">
          Some of what you chose dates its stages from the due date. Without one,
          those stages stay blank — setting the task's due date later fills them;
          otherwise they need entering by hand.
        </p>
      ) : null}

      <div className="row" style={{ justifyContent: "flex-end" }}>
        <button type="button" onClick={onDone} disabled={saving}>
          Cancel
        </button>
        <button className="primary" type="submit" disabled={saving || chosen === 0}>
          {saving
            ? "Adding…"
            : chosen === 0
              ? "Choose something to add"
              : `Add ${describe(deliverableIds.size, taskIds.size)}`}
        </button>
      </div>
    </form>
  );
}

function describe(deliverables: number, tasks: number): string {
  const parts = [];
  if (deliverables > 0) parts.push(`${deliverables} deliverable${deliverables === 1 ? "" : "s"}`);
  if (tasks > 0) parts.push(`${tasks} task${tasks === 1 ? "" : "s"}`);
  return parts.join(" and ");
}

/**
 * What happened, said in full: made, skipped (with the reason), and the
 * templated blocks that still wait for somebody to point them at a task.
 */
function AddedReport({
  result,
  onDone,
}: {
  result: AddFromTemplatesResult;
  onDone: () => void;
}) {
  const waiting = result.blocks.filter((b) => !b.linked).length;
  return (
    <div className="card">
      <h2>Added</h2>
      <p>
        {result.deliverables.length} deliverable{result.deliverables.length === 1 ? "" : "s"} and{" "}
        {result.tasks.length} task{result.tasks.length === 1 ? "" : "s"} are on the project now.
      </p>
      {result.skipped.length > 0 ? (
        <div className="admin-warn">
          <strong>Not added:</strong>
          <ul className="aft-report">
            {result.skipped.map((s, i) => (
              <li key={i}>
                {s.template} — {s.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {/* .warn, not .admin-warn: nothing went wrong, there is something to
          finish. "Not added" above is the red one. */}
      {result.awaitingDueDate.length > 0 ? (
        <div className="warn">
          <strong>Waiting for a due date:</strong>
          <ul className="aft-report">
            {result.awaitingDueDate.map((t) => (
              <li key={t.taskId}>
                {t.title} — {t.stages} stage{t.stages === 1 ? "" : "s"} counted from the due date
              </li>
            ))}
          </ul>
          <span className="muted">
            Set each task's due date and these fill in; otherwise enter them by hand.
          </span>
        </div>
      ) : null}
      {waiting > 0 ? (
        <p className="muted">
          {waiting} stage{waiting === 1 ? " is" : "s are"} blocked waiting on work that
          was not added here. Open the task and use Link it to say what it waits on.
        </p>
      ) : null}
      <div className="row" style={{ justifyContent: "flex-end" }}>
        <button className="primary" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}
